import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ChevronLeft, Crown, LogOut, ShieldCheck, Settings, Sparkles, Building2, Trophy, UserPlus, MessageCircle, Bell, Activity, Award, Target, Bluetooth, UserCircle } from "lucide-react";
import PhoneFrame from "../components/PhoneFrame.jsx";
import BottomNav from "../components/BottomNav.jsx";
import CoachPickerModal from "../components/CoachPickerModal.jsx";
import { useAuth } from "../lib/auth.jsx";

export default function Profile() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [coachPickerOpen, setCoachPickerOpen] = useState(false);
  if (!user) return null;

  return (
    <PhoneFrame>
      <div className="ps-aurora min-h-screen">
        <div className="px-5 pt-6 pb-28 relative">
          <button onClick={() => nav(-1)} className="text-slate-400 inline-flex items-center gap-1 text-sm" data-testid="profile-back">
            <ChevronLeft size={16} /> Back
          </button>

          {/* Editorial avatar hero */}
          <div className="mt-6 ps-card p-6 text-center relative overflow-hidden ps-fade-up">
            <div className="absolute -top-14 -left-14 w-44 h-44 rounded-full bg-[#4ade80]/15 blur-3xl" />
            <div className="absolute -bottom-14 -right-14 w-44 h-44 rounded-full bg-[#a78bfa]/15 blur-3xl" />
            <div className="relative">
              <div className="relative w-24 h-24 mx-auto">
                <div className="absolute inset-0 rounded-full bg-gradient-to-br from-[#4ade80] via-[#22d3ee] to-[#a78bfa] blur-md opacity-60" />
                <div className="relative w-24 h-24 rounded-full bg-gradient-to-br from-[#15303E] to-[#0E1F2A] border border-[#1B364A] flex items-center justify-center text-4xl font-extrabold ps-heading">
                  {user.name?.[0]}
                </div>
              </div>
              <div className="ps-heading text-2xl font-extrabold mt-4" data-testid="profile-name">{user.name}</div>
              <div className="text-slate-400 text-sm ps-serif ps-italic" data-testid="profile-email">{user.email}</div>
              {user.is_premium && (
                <div className="mt-3 ps-chip ps-chip-amber inline-flex">
                  <Crown size={11} /> Premium
                </div>
              )}
            </div>
          </div>

          <SectionLabel>Your journey</SectionLabel>
          <div className="space-y-2">
          {!user.is_premium && (
            <Link to="/premium" data-testid="profile-upgrade" className="ps-card p-4 flex items-center gap-3">
              <Crown size={20} color="#F59E0B" />
              <div className="flex-1">
                <div className="font-semibold">Upgrade to Premium</div>
                <div className="text-xs text-slate-400">$9.99 / month · Advanced analytics, full library, premium challenges</div>
              </div>
              <Sparkles size={16} color="#F59E0B" />
            </Link>
          )}
          <Link to="/journey" data-testid="profile-journey" className="ps-card p-4 flex items-center gap-3">
            <Trophy size={20} color="#2fbf71" />
            <div className="flex-1 font-semibold">365-Day Journey</div>
          </Link>
          <Link to="/annual-goal" data-testid="profile-annual" className="ps-card p-4 flex items-center gap-3">
            <Target size={20} color="#F59E0B" />
            <div className="flex-1">
              <div className="font-semibold">Annual Goal</div>
              <div className="text-xs text-slate-400">Set your yearly step target</div>
            </div>
          </Link>
          <Link to="/badges" data-testid="profile-badges" className="ps-card p-4 flex items-center gap-3">
            <Award size={20} color="#F59E0B" />
            <div className="flex-1 font-semibold">Badges</div>
          </Link>
          <Link to="/challenges" data-testid="profile-challenges" className="ps-card p-4 flex items-center gap-3">
            <Trophy size={20} color="#F59E0B" />
            <div className="flex-1 font-semibold">Challenges</div>
          </Link>

          </div>
          <SectionLabel>Social</SectionLabel>
          <div className="space-y-2">
          <Link to="/community" data-testid="profile-community" className="ps-card p-4 flex items-center gap-3">
            <Sparkles size={20} color="#00B4D8" />
            <div className="flex-1 font-semibold">Community</div>
          </Link>
          <Link to="/friends" data-testid="profile-friends" className="ps-card p-4 flex items-center gap-3">
            <UserPlus size={20} color="#2fbf71" />
            <div className="flex-1 font-semibold">Friends</div>
          </Link>
          <Link to="/messages" data-testid="profile-messages" className="ps-card p-4 flex items-center gap-3">
            <MessageCircle size={20} color="#00B4D8" />
            <div className="flex-1 font-semibold">Messages</div>
          </Link>

          </div>
          <SectionLabel>Settings</SectionLabel>
          <div className="space-y-2">
          <Link to="/notifications" data-testid="profile-notifications" className="ps-card p-4 flex items-center gap-3">
            <Bell size={20} color="#F59E0B" />
            <div className="flex-1 font-semibold">Notifications</div>
          </Link>
          <Link to="/settings/notifications" data-testid="profile-prefs" className="ps-card p-4 flex items-center gap-3">
            <Settings size={20} color="#94A3B8" />
            <div className="flex-1">
              <div className="font-semibold">Notification Preferences</div>
              <div className="text-xs text-slate-400 ps-serif ps-italic">Mute per type</div>
            </div>
          </Link>
          <Link to="/settings/integrations" data-testid="profile-integrations" className="ps-card p-4 flex items-center gap-3">
            <Activity size={20} color="#2fbf71" />
            <div className="flex-1">
              <div className="font-semibold">Health Integrations</div>
              <div className="text-xs text-slate-400 ps-serif ps-italic">Apple Health · Google Health Connect · Fitbit</div>
            </div>
          </Link>
          <Link to="/devices" data-testid="profile-devices" className="ps-card p-4 flex items-center gap-3">
            <Bluetooth size={20} color="#4ade80" />
            <div className="flex-1">
              <div className="font-semibold">Back Transformer™</div>
              <div className="text-xs text-slate-400 ps-serif ps-italic">Pair · biomechanic telemetry</div>
            </div>
          </Link>
          <button data-testid="profile-coach-picker" onClick={() => setCoachPickerOpen(true)} className="ps-card p-4 flex items-center gap-3 w-full text-left">
            <UserCircle size={20} color="#a78bfa" />
            <div className="flex-1">
              <div className="font-semibold">Choose your Coach</div>
              <div className="text-xs text-slate-400 ps-serif ps-italic">Pick a persona & voice</div>
            </div>
          </button>
          {user.employer_code && (
            <Link to="/corporate" data-testid="profile-corp" className="ps-card p-4 flex items-center gap-3">
              <Building2 size={20} color="#2fbf71" />
              <div className="flex-1">
                <div className="font-semibold">Corporate Dashboard</div>
                <div className="text-xs text-slate-400 ps-serif ps-italic">{user.employer_code}</div>
              </div>
            </Link>
          )}
          {user.role === "admin" && (
            <Link to="/admin" data-testid="profile-admin" className="ps-card p-4 flex items-center gap-3">
              <ShieldCheck size={20} color="#2fbf71" />
              <div className="flex-1 font-semibold">Admin Console</div>
            </Link>
          )}
          <button data-testid="profile-logout" onClick={logout} className="w-full ps-card p-4 flex items-center gap-3 text-[#EF4444] hover:border-[#EF4444]/40 transition-colors">
            <LogOut size={20} />
            <div className="flex-1 font-semibold text-left">Log out</div>
          </button>
        </div>
        </div>
      </div>
      <BottomNav />
      <CoachPickerModal open={coachPickerOpen} onClose={() => setCoachPickerOpen(false)} />
    </PhoneFrame>
  );
}

function SectionLabel({ children }) {
  return (
    <div className="mt-6 mb-3 flex items-center gap-3">
      <div className="text-[10px] uppercase tracking-[0.3em] text-slate-400 font-bold">
        {children}
      </div>
      <div className="flex-1 h-px bg-gradient-to-r from-[#1B364A] to-transparent" />
    </div>
  );
}
